import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil } from "lucide-react";
import { useBundle } from "@/features/bundles/hooks/use-bundle";
import { getManagedEventsBy } from "@/features/supply-config/managed-events-api";
import { ContentLayout } from "@/components/common/content-layout";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BundleCardPreview from "@/features/bundles/ui/bundle-card-preview";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="text-sm text-gray-900 mt-0.5 break-words">
        {value || "—"}
      </div>
    </div>
  );
}

export default function BundleDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: bundle, isLoading, isFetching, error } = useBundle(id);

  const { data: managedEvents = [] } = useQuery({
    queryKey: ["managed-events-by", bundle?.product_id, bundle?.region_id],
    queryFn: () =>
      getManagedEventsBy({
        product_id: bundle?.product_id ?? undefined,
        region_id: bundle?.region_id ?? undefined,
      }),
    enabled: !!bundle?.product_id && !!bundle?.region_id,
  });
  const nameById = new Map(managedEvents.map((m) => [m.id, m.name]));

  const sessions = [...(bundle?.session_types ?? [])].sort(
    (a, b) => a.priority - b.priority,
  );

  const expiry = bundle?.is_expiration_in_days
    ? bundle?.expires_in_day
      ? `${bundle.expires_in_day} day(s)`
      : "—"
    : bundle?.expires_in_month
      ? `${bundle.expires_in_month} month(s)`
      : "No expiry";

  const overviewCard = bundle ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          <Meta label="Product" value={bundle.product?.name} />
          <Meta
            label="Region"
            value={
              bundle.region_rel
                ? `${codeToFlag(bundle.region_rel.code)} ${bundle.region_rel.display_name}`
                : (bundle.region ?? "—")
            }
          />
          <Meta label="Type" value={bundle.type} />
          <Meta
            label="Price"
            value={`${bundle.currency} ${(bundle.amount ?? 0).toLocaleString()}`}
          />
          <Meta label="Reschedule amount" value={bundle.reschedule_amount} />
          <Meta label="Sessions" value={bundle.session_quantity ?? "—"} />
          <Meta
            label="Total allowed users"
            value={bundle.total_allowed_user ?? "—"}
          />
          <Meta label="User payment type" value={bundle.user_payment_type} />
          <Meta label="Expiry" value={expiry} />
          <Meta
            label="Payment page"
            value={
              bundle.payment_page_link ? (
                <a
                  href={bundle.payment_page_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Open ↗
                </a>
              ) : (
                "—"
              )
            }
          />
        </div>
      </CardContent>
    </Card>
  ) : null;

  const sessionsCard = bundle ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Sessions ({sessions.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {sessions.length === 0 ? (
          <p className="text-sm text-gray-400">No session types.</p>
        ) : (
          <div className="border border-gray-200 rounded-md divide-y">
            {sessions.map((s, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                <Badge variant="outline">{s.priority}</Badge>
                <span className="font-medium flex-1 truncate">
                  {nameById.get(s.managed_event_id) ?? s.managed_event_id}
                </span>
                <span className="text-xs text-gray-500">
                  {s.booked_by === "PRIMARY_USER"
                    ? "Primary user"
                    : "Any other user"}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  ) : null;

  const formsCard = bundle ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Forms ({bundle.forms?.length ?? 0})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {bundle.forms?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {bundle.forms.map((f) => (
              <Badge key={f.id} variant="outline">
                {f.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No forms.</p>
        )}
      </CardContent>
    </Card>
  ) : null;

  const couponsCard = bundle ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Coupons ({bundle.coupons?.length ?? 0})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {bundle.coupons?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {bundle.coupons.map((c) => (
              <Badge key={c.id} variant="outline">
                {c.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No coupons.</p>
        )}
      </CardContent>
    </Card>
  ) : null;

  return (
    <ContentLayout
      data={bundle ? [bundle] : []}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      contentClassName="space-y-6"
      errorMessage="Failed to load bundle"
      emptyState={{ message: "Bundle not found" }}
    >
      {bundle && (
        <div className="w-full space-y-6">
          <button
            type="button"
            onClick={() => navigate("..")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" /> Bundles
          </button>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900">
                {bundle.name}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                {bundle.type && (
                  <Badge variant="outline" className="capitalize">
                    {bundle.type}
                  </Badge>
                )}
                {bundle.region_rel && (
                  <Badge variant="outline" className="uppercase text-[10px]">
                    {codeToFlag(bundle.region_rel.code)} {bundle.region_rel.code}
                  </Badge>
                )}
                <Badge
                  variant={bundle.status === "ACTIVE" ? "active" : "inactive"}
                  className="uppercase text-[10px]"
                >
                  {bundle.status}
                </Badge>
              </div>
              <p className="text-xs font-mono text-gray-400 mt-1 break-all">
                standard_id {bundle.standard_id}
              </p>
            </div>
            <HasAccess permission="write">
              <Button variant="outline" size="sm" onClick={() => navigate("edit")}>
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
            </HasAccess>
          </div>

          {bundle.show_to_public ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-6 items-start">
              <div className="space-y-6 min-w-0">
                {overviewCard}
                {sessionsCard}
              </div>
              {/* Sidebar: preview + the extras below it fill the vertical space. */}
              <div className="space-y-6">
                <BundleCardPreview bundle={bundle} />
                {formsCard}
                {couponsCard}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {overviewCard}
              {sessionsCard}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
                {formsCard}
                {couponsCard}
              </div>
            </div>
          )}
        </div>
      )}
    </ContentLayout>
  );
}
